import React, { useState, useEffect } from 'react';
import './MUMTable.css';
import apiClient from '../../apiClient';

const MUMTable = ({ selectedMUM, onDataFetch }) => {
  const [storeData, setStoreData] = useState([]);
  const [staffingData, setStaffingData] = useState({});
  const [awsSales, setAwsSales] = useState({});
  const [awsManagement, setAwsManagement] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Position types we're tracking
  const positionTypes = ['RM', 'AM', 'SL', 'CM'];

  useEffect(() => {
    if (!selectedMUM) return;

    const fetchStoreData = async () => {
      setLoading(true);
      setError(null);
      try {
        // First, get the list of stores for this MUM
        const storesResponse = await apiClient.get(
          `/api/current-staffing?mum=${encodeURIComponent(selectedMUM)}&ts=${Date.now()}`
        );

        if (storesResponse.status === 200) {
          const stores = storesResponse.data;
          setStoreData(stores);
          
          // Now fetch staffing data for all stores in this MUM in one call
          const staffingResponse = await apiClient.get(
            `/api/store-staffing-counts?mum=${encodeURIComponent(selectedMUM)}&ts=${Date.now()}`
          );
          
          if (staffingResponse.status === 200) {
            setStaffingData(staffingResponse.data);
          } else {
            console.error("Failed to fetch staffing data:", staffingResponse.statusText);
            setError("Failed to fetch staffing data. Please try again.");
          }
        } else {
          console.error("Failed to fetch store data:", storesResponse.statusText);
          setError("Failed to fetch store data. Please try again.");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Error fetching data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    // Fetch AWS sales data from the new endpoint
    const fetchAwsSalesData = async () => {
      try {
        const awsResponse = await apiClient.get(
          `/api/aws-sales?mum=${encodeURIComponent(selectedMUM)}&ts=${Date.now()}`
        );
        if (awsResponse.status === 200) {
          // Transform the array into objects keyed by store corp
          const salesMapping = {};
          const managementMapping = {};
          
          awsResponse.data.forEach(item => {
            salesMapping[item.corp] = item.average_weekly_sales;
            managementMapping[item.corp] = {
              rm: item.rm || 0,
              am: item.am || 0,
              sl: item.sl || 0,
              total_management: item.total_management || 0,
              average_weekly_sales: item.average_weekly_sales || 0
            };
          });
          
          setAwsSales(salesMapping);
          setAwsManagement(managementMapping);
        } else {
          console.error("Failed to fetch AWS sales data:", awsResponse.statusText);
        }
      } catch (error) {
        console.error("Error fetching AWS sales data:", error);
      }
    };

    fetchStoreData();
    fetchAwsSalesData();
  }, [selectedMUM]);

  // Pass AWS data to parent component whenever it updates
  useEffect(() => {
    if (onDataFetch && Object.keys(awsSales).length > 0) {
      // Enrich storeData with AWS information
      const enrichedStoreData = storeData.map(store => ({
        ...store,
        aws: awsSales[store.corp] || 0,
        is24Hours: store.is24Hours || false,
      }));
      onDataFetch(enrichedStoreData);
    }
  }, [awsSales, storeData, onDataFetch]);

  // Helper to format numbers as currency with commas
  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return Number(value).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  // Helper function to determine staffing target based on AWS
  const getStaffingTarget = (aws) => {
    // Calculate target by dividing AWS by 1250
    const target = Math.round(aws / 1250);
    // Return at least 1 as minimum staffing
    return Math.max(1, target);
  };

  // Helper function for gap indicators
  const getGapIndicator = (ideal, actual, positionType) => {
    const gap = actual - ideal;
    
    // Critical: Missing RM or AM positions
    if ((positionType === 'RM' || positionType === 'AM') && gap < 0) {
      return { value: gap, status: 'critical', icon: '🔴' };
    }
    
    // Warning: Significantly understaffed or overstaffed
    if (gap < -1) {
      return { value: gap, status: 'critical', icon: '🔴' };
    }
    
    if (gap > 1) {
      return { value: `+${gap}`, status: 'warning', icon: '⚠️' };
    }
    
    // Good: Matched
    if (gap === 0) {
      return { value: 0, status: 'good', icon: '✓' };
    }
    
    // Minor variance
    if (gap === 1) {
      return { value: `+${gap}`, status: 'minor', icon: '' };
    }
    
    if (gap === -1) {
      return { value: gap, status: 'warning', icon: '⚠️' };
    }
    
    return { value: gap > 0 ? `+${gap}` : gap, status: 'minor', icon: '' };
  };

  if (loading) {
    return <div className="loading-indicator">Loading data...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="mum-table">
      <table>
        <thead>
          <tr>
            <th>
              Store Name
              <span className="info-tooltip">
                <span className="info-icon">?</span>
                <span className="tooltip-content">
                  <p>Staffing Guidelines:</p>
                  <ul>
                    <li>Under 25k: 1 RM, 1 SL</li>
                    <li>25-30k: 1 RM, 2 SL</li>
                    <li>30-39k: 1 RM, 1 AM, 2 SLs</li>
                    <li>40k+: 1 RM, 1 AM, 3 SLs (if 24 hours, then 4 SLs)</li>
                    <li>Higher than 50k may be able to have more leadership</li>
                  </ul>
                </span>
              </span>
            </th>
            {storeData.map((store) => (
              <th key={store.corp}>{store.corp}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Quarterly AWS $ */}
          <tr>
            <td>Quarterly AWS $</td>
            {storeData.map((store) => (
              <td key={store.corp}>
                <span className="aws-value">
                  {awsSales[store.corp] !== undefined
                    ? formatCurrency(awsSales[store.corp])
                    : "$0.00"}
                </span>
              </td>
            ))}
          </tr>

          {/* Staffing Target */}
          <tr className="staffing-target-row">
            <td>Staffing Target (CM Only)</td>
            {storeData.map((store) => (
              <td key={store.corp}>
                {getStaffingTarget(awsSales[store.corp] || 0)}
              </td>
            ))}
          </tr>

          {/* Current Staffing Header */}
          <tr className="current-staffing-header">
            <td colSpan={storeData.length + 1} className="current-staffing-label">
              Current Staffing
            </td>
          </tr>

          {/* Column Headers for Current Staffing */}
          <tr className="staffing-column-headers">
            <td></td>
            {storeData.map((store) => (
              <td key={`${store.corp}-header`}>
                <div className="column-header-group">
                  <span className="header-ideal">Ideal</span>
                  <span className="header-divider">|</span>
                  <span className="header-actual">Actual</span>
                  <span className="header-divider">|</span>
                  <span className="header-gap">Gap</span>
                </div>
              </td>
            ))}
          </tr>
          
          {/* Enhanced Position Rows with Ideal | Actual | Gap */}
          {positionTypes.map(position => {
            return (
              <tr key={position} className={`${position.toLowerCase()}-row staffing-row`}>
                <td className="position-type">{position}</td>
                {storeData.map((store) => {
                  const ideal = position === 'CM' 
                    ? getStaffingTarget(awsManagement[store.corp]?.average_weekly_sales || 0)
                    : awsManagement[store.corp]?.[position.toLowerCase()] || 0;
                  const actual = staffingData[store.corp]?.[position] || 0;
                  const gap = getGapIndicator(ideal, actual, position);
                  
                  return (
                    <td key={`${store.corp}-${position}`} className="staffing-cell">
                      <div className="staffing-metrics">
                        <span className="ideal">{ideal}</span>
                        <span className="divider">|</span>
                        <span className="actual">{actual}</span>
                        <span className="divider">|</span>
                        <span className={`gap ${gap.status}`}>
                          {gap.value} {gap.icon}
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}

          {/* Management Summary Row */}
          <tr className="management-summary-row">
            <td>Management Total</td>
            {storeData.map((store) => {
              const idealTotal = awsManagement[store.corp]?.total_management || 0;
              const actualTotal = (staffingData[store.corp]?.RM || 0) + 
                                 (staffingData[store.corp]?.AM || 0) + 
                                 (staffingData[store.corp]?.SL || 0);
              const gap = actualTotal - idealTotal;
              
              return (
                <td key={store.corp}>
                  <div className="staffing-metrics">
                    <span className="ideal">{idealTotal}</span>
                    <span className="divider">|</span>
                    <span className="actual">{actualTotal}</span>
                    <span className="divider">|</span>
                    <span className={gap < 0 ? 'gap critical' : gap > 0 ? 'gap warning' : 'gap good'}>
                      {gap > 0 ? `+${gap}` : gap === 0 ? '0' : gap}
                      {gap < 0 ? ' 🔴' : gap > 0 ? ' ⚠️' : ' ✓'}
                    </span>
                  </div>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default MUMTable;